import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Input, Message, Icon } from "semantic-ui-react";
import CustomToaster from "../../components/CustomToaster";
import {
  saveOpenAIKey,
  getOpenAIKey,
  deleteOpenAIKey,
  updateOrganization,
  fetchOrganizationDetails,
} from "../../actions/adminConsoleAction";
import Loader from "../../components/Loader";
import ReferenceDatasetSettings from "./ReferenceDatasetSettings";
import ExternalDatasetSettings from "./ExternalDatasetSettings";
import "../../styles/Configuration.css"; // Your CSS

const Configuration = () => {
  const dispatch = useDispatch();

  // Redux state
  const openAIKeyFromStore = useSelector((state) => state.admin?.openAIKey ?? "");
  const saveOpenAIKeyStatus = useSelector((state) => state.admin?.saveOpenAIKeyStatus ?? null);
  const deleteOpenAIKeyStatus = useSelector((state) => state.admin?.deleteOpenAIKeyStatus ?? null);
  const getOpenAIKeyStatus = useSelector((state) => state.admin?.getOpenAIKeyStatus ?? null);
  const orgDetails = useSelector((state) => state.admin.details);

  // Local states
  const [apiKey, setApiKey] = useState("");
  const [message, setMessage] = useState(null);
  const [toaster, setToaster] = useState({ visible: false, type: "", content: "" });
  const [toasterVisible, setToasterVisible] = useState(false);
  const [toasterMessage, setToasterMessage] = useState("");
  const [toasterAppearance, setToasterAppearance] = useState("success");
  
  // Usage Settings states
  const [maxQuestionsPerSession, setMaxQuestionsPerSession] = useState(30);
  const [costPerSession, setCostPerSession] = useState(3.0);
  const [maxTokenUsagePerUserPerMonth, setMaxTokenUsagePerUserPerMonth] = useState(100000);
  const [usageSettingsLoading, setUsageSettingsLoading] = useState(false); 
  const [isSaveDisabled, setIsSaveDisabled] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const showToaster = (message, appearance) => {
    setToasterVisible(true);
    setToasterMessage(message);
    setToasterAppearance(appearance);
  };

  const closeToaster = () => {
    setToasterVisible(false);
  };

  // Fetch API key on mount
  useEffect(() => {
    const fetchKey = async () => {
      setShowLoader(true);
      try {
        const response = await dispatch(getOpenAIKey());
        // If the GET API returns a 409 conflict, show the unique-key toaster message.
        if (response?.status === 409) {
          showToaster('Please Enter a Unique Key', 'error');
        }
      } catch (error) {
        console.error("Error fetching API Key:", error);
      } finally {
        setShowLoader(false);
      }
    };
    fetchKey();
  }, [dispatch]);

  // Update local states after GET
  useEffect(() => {
    if (typeof openAIKeyFromStore === "string" && openAIKeyFromStore.trim() !== "") {
      setApiKey(openAIKeyFromStore);
      setIsSaveDisabled(true);
    } else {
      setApiKey("");
      setIsSaveDisabled(false);
    }
  }, [openAIKeyFromStore]);

  // Initialize usage settings from org details
  useEffect(() => {
    if (orgDetails) {
      setMaxQuestionsPerSession(orgDetails.maxQuestionsPerSession ?? 30);
      setCostPerSession(orgDetails.costPerSession ?? 3.0);
      setMaxTokenUsagePerUserPerMonth(orgDetails.maxTokenUsagePerUserPerMonth ?? 100000);
    }
  }, [orgDetails]);

  // Helper for showing temporary toaster messages
  // const showToaster = (type, content) => {
  //   setToaster({ visible: true, type, content });
  //   setTimeout(() => {
  //     setToaster({ visible: false, type: "", content: "" });
  //   }, 3000);
  // };

  // Compute whether the Delete button should be disabled:
  // It is disabled if there is no API key (i.e. input is empty)
  const isDeleteDisabled = !apiKey || apiKey.trim() === "";

  // Save handler
  const handleSave = async () => {
    if (apiKey.trim() === "") {
      setMessage({ type: "error", content: "API Key cannot be empty." });
      return;
    }
    setShowLoader(true);
    try {
      const response = await dispatch(saveOpenAIKey(apiKey));
      // Check if the response status indicates a conflict
      if (response?.status === 409) {
        showToaster('Please Enter a Unique Key', 'error');
      } else {
        setIsSaveDisabled(true);
        showToaster("Key successfully added", "success");
      }
    } catch (error) {
      setMessage({ type: "error", content: "Failed to save API Key. Try again." });
      showToaster('Failed to save API Key', 'error');
    } finally {
      setShowLoader(false);
    }
  };

  // Delete handler
  const handleDelete = async () => {
    setShowLoader(true);
    try {
      await dispatch(deleteOpenAIKey());
      setApiKey("");
      setIsSaveDisabled(false);
      showToaster("Key successfully deleted", "success");
    } catch (error) {
      setMessage({ type: "error", content: "Failed to delete API Key. Try again." });
      showToaster('Failed to delete API Key', 'error');
    } finally {
      setShowLoader(false);
    }
  };

  // Save Usage Settings handler
  const handleSaveUsageSettings = async () => {
    if (maxQuestionsPerSession < 1 || maxQuestionsPerSession > 100) {
      showToaster('Max Questions Per Session must be between 1 and 100', 'error');
      return;
    }
    if (costPerSession < 0) {
      showToaster('Cost Per Session cannot be negative', 'error');
      return;
    }
    if (maxTokenUsagePerUserPerMonth < 0) {
      showToaster('Max Token Usage Per User cannot be negative', 'error');
      return;
    }

    setUsageSettingsLoading(true);
    try {
      const organizationId = orgDetails?._id;
      const organizationData = {
        ...orgDetails,
        orgId: organizationId,
        maxQuestionsPerSession: parseInt(maxQuestionsPerSession, 10),
        costPerSession: parseFloat(costPerSession),
        maxTokenUsagePerUserPerMonth: parseInt(maxTokenUsagePerUserPerMonth, 10)
      };
      await dispatch(updateOrganization(organizationData));
      await dispatch(fetchOrganizationDetails(true));
      showToaster("Usage settings saved successfully", "success");
    } catch (error) {
      console.error("Error saving usage settings:", error);
      showToaster("Failed to save usage settings", "error");
    } finally {
      setUsageSettingsLoading(false);
    }
  };

  // Reset Usage Settings to defaults
  const handleResetUsageSettings = () => {
    setMaxQuestionsPerSession(30);
    setCostPerSession(3.0);
    setMaxTokenUsagePerUserPerMonth(100000);
    showToaster("Usage settings reset to defaults", "success");
  };

  return (
    <>
        {toasterVisible && (
            <CustomToaster
              message={toasterMessage}
              duration={3000}
              onClose={closeToaster}
              position="right-top"
              appearance={toasterAppearance}
            />
          )}
    <div className="configuration-page">
      {showLoader && <Loader />}

      <div className="configuration-content-container full-width">
        <div className="configuration-segment">
          {toaster.visible && (
            <div className={`toaster-message ${toaster.type}`}>
              {toaster.content}
            </div>
          )}

          <h2 style={{ marginBottom: "20px" }}>Organization Settings</h2>

          {message && (
            <Message
              positive={message.type === "success"}
              negative={message.type === "error"}
              content={message.content}
              style={{ marginBottom: "20px" }}
            />
          )}

          <div className="config-item">
            <label style={{ display: "block", marginBottom: "8px" }}>
              OpenAI API Key
            </label>
            <Input
              type={showPassword ? "text" : "password"}
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              icon={
                <Icon
                  name={showPassword ? "eye slash" : "eye"}
                  link
                  onClick={() => setShowPassword((prev) => !prev)}
                />
              }
              fluid
              disabled={isSaveDisabled}
            />
          </div>

          <div className="button-group" style={{ marginTop: "15px" }}>
            <Button
              className={`save-button ${isSaveDisabled ? "disabled-button" : ""}`}
              onClick={handleSave}
              disabled={isSaveDisabled}
              style={{ minWidth: "80px" }}
            >
              Save
            </Button>

            <Button
              className="delete-button"
              onClick={handleDelete}
              disabled={isDeleteDisabled}
              style={{ minWidth: "80px" }}
            >
              Delete
            </Button>
          </div>
        </div>
        
        {/* Reference Dataset Settings Section */}
        <ReferenceDatasetSettings showToaster={showToaster} />
        <ExternalDatasetSettings showToaster={showToaster}
        setShowLoader={setShowLoader}  />

        {/* Usage Settings */}
        <div className="configuration-segment" style={{ marginTop: "30px" }}>
          <h2 style={{ marginBottom: "20px" }}>Usage Settings</h2>
          
          <div style={{ display: "flex", gap: "20px", flexWrap: "wrap" }}>
            <div className="config-item" style={{ flex: "1", minWidth: "200px" }}>
              <label style={{ display: "block", marginBottom: "8px" }}>
                Max Questions Per Session
              </label>
              <Input
                type="number"
                min={1}
                max={100}
                value={maxQuestionsPerSession}
                onChange={(e) => setMaxQuestionsPerSession(e.target.value)}
                fluid
              />
              <span style={{ fontSize: "14px", color: "#97A3BA", marginTop: "4px", display: "block" }}>
                Number of questions before a new session starts
              </span>
            </div>

            <div className="config-item" style={{ flex: "1", minWidth: "200px" }}>
              <label style={{ display: "block", marginBottom: "8px" }}>
                Cost Per Session ($)
              </label>
              <Input
                type="number"
                min={0}
                step={0.01}
                value={costPerSession}
                onChange={(e) => setCostPerSession(e.target.value)}
                fluid
              />
              <span style={{ fontSize: "14px", color: "#97A3BA", marginTop: "4px", display: "block" }}>
                Cost per session for usage reporting
              </span>
            </div>

            <div className="config-item" style={{ flex: "1", minWidth: "200px" }}>
              <label style={{ display: "block", marginBottom: "8px" }}>
                Max Token Usage Per User Per Month
              </label>
              <Input
                type="number"
                min={0}
                value={maxTokenUsagePerUserPerMonth}
                onChange={(e) => setMaxTokenUsagePerUserPerMonth(e.target.value)}
                fluid
              />
              <span style={{ fontSize: "14px", color: "#97A3BA", marginTop: "4px", display: "block" }}>
                Maximum tokens a user can consume per month
              </span>
            </div>
          </div>

          <div className="button-group" style={{ marginTop: "15px" }}>
            <Button
              className="save-button"
              onClick={handleSaveUsageSettings}
              disabled={usageSettingsLoading}
              loading={usageSettingsLoading}
              style={{ minWidth: "80px" }}
            >
              Save
            </Button>

            <Button
              className="delete-button"
              onClick={handleResetUsageSettings}
              disabled={usageSettingsLoading}
              style={{ minWidth: "80px" }}
            >
              Reset
            </Button>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default Configuration;
